import type { RdaSuggestion, RdaTypeCodes, RdaVocabularies, RecordType } from "@/api/types";

const ELEMENTS = ["contentTypes", "mediaTypes", "carrierTypes"] as const;

interface ProposeRdaTypesInput {
  /** RDA-FR values currently in the form. */
  current: RdaTypeCodes;
  /** Document type currently selected in the form. */
  documentType: RecordType;
  /** Saved record, when editing one. */
  saved?: { type: RecordType; rdaSuggestion: RdaSuggestion | null } | undefined;
  /**
   * Usual types by document type. Optional: a vocabulary response cached
   * before this field existed must not break the editor.
   */
  defaultsByDocumentType?: RdaVocabularies["defaultsByDocumentType"] | undefined;
}

/**
 * Values to propose for the empty RDA-FR elements of the form.
 *
 * The server's proposal for the saved record comes first: it can use the
 * 181/182/183 fields kept from an imported source record. It no longer
 * applies once the document type is changed, and does not cover elements the
 * cataloguer has just emptied; the usual values of the document type are
 * used there. Returns null when there is nothing to propose.
 */
export function proposeRdaTypes({
  current,
  documentType,
  saved,
  defaultsByDocumentType,
}: ProposeRdaTypesInput): RdaSuggestion | null {
  const server = saved?.type === documentType ? saved.rdaSuggestion : null;
  const defaults = defaultsByDocumentType?.[documentType];

  const proposal: RdaSuggestion = {
    contentTypes: [],
    mediaTypes: [],
    carrierTypes: [],
    fromSourceRecord: false,
    inferred: false,
  };

  for (const element of ELEMENTS) {
    if (current[element].length > 0) continue;

    const fromServer = server?.[element] ?? [];
    if (fromServer.length > 0 && server) {
      proposal[element] = fromServer;
      proposal.fromSourceRecord ||= server.fromSourceRecord;
      proposal.inferred ||= server.inferred;
    } else if (defaults && defaults[element].length > 0) {
      proposal[element] = defaults[element];
      proposal.inferred = true;
    }
  }

  return ELEMENTS.some((element) => proposal[element].length > 0) ? proposal : null;
}
